import { DocumentType, GenderType, PassengerType } from '@/shared/api/aviabooking'
import * as yup from 'yup'
import { BookFlightFormValues } from './types';
import { E164_REGEX } from '@/shared/consts';

const calculateAge = (birthDate: string): number => {
    const bDate = new Date(birthDate);
    const today = new Date();

    let age = today.getFullYear() - bDate.getFullYear();
    const monthDiff = today.getMonth() - bDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < bDate.getDate())) {
        age--;
    }

    return age
}

const InternationalPassportRegex = /^[0-9]{9}$/
const PassportRegex = /^[0-9]{10}$/
const BirthCertificateRegex = /^[IVX]{2}[А-Я]{2}[0-9]{6}$/

const convertToJsDate = (dateString: string) => {
    const dateRU = dateString.replace(/\./g, '/')
    const parts = dateRU.split('/')

    const day = parts[0]
    const month = parts[1]
    const year = parts[2]

    return `${month}/${day}/${year}`
}

export const bookFlightValidationSchema = yup.object().shape({
    phone: yup.string().matches(E164_REGEX, 'Некорректный номер телефона'),
    travellers: yup
        .array()
        .min(1, "Укажите данные пассажиров")
        .of(
            yup.object().shape({
                id: yup.number().typeError("Некорректный ID пассажира").required("Укажите ID пассажира"),
                type: yup
                    .string()
                    .oneOf([PassengerType.ADT, PassengerType.CNN, PassengerType.INF], "Некорректный тип пассажира")
                    .required("Укажите тип пассажира"),
                first_name: yup.string()
                    .matches(/^[A-Za-z]+$/, "Имя должно содержать только латинские буквы")
                    .min(1, "Укажите имя пассажира")
                    .required("Укажите имя пассажира"),
                last_name: yup.string()
                    .matches(/^[A-Za-z]+$/, "Фамилия должна содержать только латинские буквы")
                    .min(1, "Укажите фамилию пассажира")
                    .required("Укажите фамилию пассажира"),
                middle_name: yup.string()
                    .matches(/^[A-Za-z]+$/, "Отчество должно содержать только латинские буквы")
					.transform(value => !value ? undefined : value)
                    .optional(),
                date_of_birth: yup
                    .string()
                    .min(10, 'Укажите дату рождения')
                    .required('Укажите дату рождения')
                    .test('is-valid-date', 'Некорректный формат даты рождения', (value) => {
                        if (!value) return false;
                        const dateStr = convertToJsDate(value)
                        const date = new Date(dateStr)
                        return !isNaN(date.getTime())
                    })
                    .when('type', (type, schema) => {
                        if (type.includes(PassengerType.ADT)) {
                            return schema.test('is-adt', 'Возраст пассажира должен быть больше 12 лет', (value) => {
                                if (!value) return false;
                                const age = calculateAge(convertToJsDate(value))
                                return age >= 12;
                            })
                        }
                        if (type.includes(PassengerType.CNN)) {
                            return schema.test('is-cnn', 'Возраст пассажира должен быть больше 2 и меньше 12 лет', (value) => {
                                if (!value) return false;
                                const age = calculateAge(convertToJsDate(value))
                                return age >= 2 && age <= 11;
                            })
                        }
                        if (type.includes(PassengerType.INF)) {
                            return schema.test('is-inf', 'Возраст пассажира должен быть меньше 2 лет', (value) => {
                                if (!value) return false;
                                const age = calculateAge(convertToJsDate(value))
                                return age >= 0 && age <= 2;
                            })
                        }
                    }),
                nationality: yup.string().required("Укажите гражданство"),
                gender: yup.string().oneOf([GenderType.Male, GenderType.Female], "Укажите пол пассажира").required("Укажите пол пассажира"),
                document_type: yup
                    .string()
                    .when('type', (type, schema) => {
                        if (type.includes(PassengerType.ADT)) {
                            return schema
                                .oneOf([
                                    DocumentType.InternationalPassportRU,
                                    DocumentType.Passport
                                ], "Некорректный тип документа")
                        }
                        if (type.includes(PassengerType.CNN)) {
                            return schema
                                .oneOf([
                                    DocumentType.InternationalPassportRU,
                                    DocumentType.BerthRegDocument,
                                    DocumentType.Passport
                                ], "Некорректный тип документа")
                        }
                        if (type.includes(PassengerType.INF)) {
                            return schema
                                .oneOf([
                                    DocumentType.BerthRegDocument,
                                ], "Некорректный тип документа")
                        }
                    })
                    .required("Укажите тип документа"),
                issue_country_code: yup.string().required("Укажите страну, в которой был выдан документ"),
                save_document: yup.boolean().optional(),
                document_number: yup
                    .string()
                    .when('document_type', (type, schema) => {
                        if (type.includes(DocumentType.BerthRegDocument)) {
                            return schema
                                .min(10, "Укажите номер документа")
                                .matches(BirthCertificateRegex, "Укажите корректные данные свидетельства о рождении")
                        }
                        if (type.includes(DocumentType.InternationalPassportRU)) {
                            return schema.min(9, "Укажите номер документа")
                                .matches(InternationalPassportRegex, "Укажите корректные данные загран паспорта")
                        }
                        if (type.includes(DocumentType.Passport)) {
                            return schema.min(10, "Укажите номер документа")
                                .matches(PassportRegex, "Укажите корректные данные пасспорта")
                        }
                    })
                    .required("Укажите номер документа"),
                expired_at: yup
                    .string()
                    .when('document_type', (type, schema) => {
                        if (type.includes(DocumentType.BerthRegDocument)) {
                            return schema
                                .optional()
                        }
                        if (type.includes(DocumentType.InternationalPassportRU)) {
                            return schema
                                .min(10, 'Укажите дату окончания срока действия')
                                .required('Укажите дату окончания срока действия')
                                .test('is-valid-date', 'Некорректный формат даты', (value) => {
                                    if (!value) return false;
                                    const dateParts = value.split('.')
                                    const date = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0])
                                    return !isNaN(date.getTime())
                                })
                                .test('is-expired', 'Срок действия документа истек', (value) => {
                                    if (!value) return false;
                                    const dateParts = value.split('.')
                                    const date = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0])
                                    return date.getTime() > new Date().getTime()
                                })
                        }
                        if (type.includes(DocumentType.Passport)) {
                            return schema
                                .min(10, 'Укажите дату выдачи документа')
                                .required('Укажите дату выдачи документа')
                                .test('is-valid-date', 'Некорректный формат даты', (value) => {
                                    if (!value) return false;
                                    const dateParts = value.split('.')
                                    const date = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0])
                                    return !isNaN(date.getTime())
                                })
                                .test('is-past-date', 'Дата выдачи не может быть в будущем', (value) => {
                                    if (!value) return false;
                                    const dateParts = value.split('.')
                                    const date = new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0])
                                    return date.getTime() <= new Date().getTime()
                                })
                        }
                    }),
                selected_seat: yup
                    .mixed()
                    .optional()
            })
        )
        .required("Укажите данные пассажиров")
}) as yup.ObjectSchema<BookFlightFormValues>
